import type { DataRetentionRow, LegalDocument } from '@/features/legal/tipos';

/**
 * Textos legales en español. **Esta es la versión vigente y vinculante**: los
 * avisos citan la LFPDPPP mexicana y se redactaron en español. Las traducciones
 * son de cortesía y así lo dicen en pantalla (ver `avisoTraduccion`).
 *
 * Transcritos literalmente desde los modales del sitio publicado en
 * `ilumetria.com` y desde `cuponera.ilumetria.com`. No confundir los documentos
 * de ILUMETRIA con los de Bar Bunny: son de responsables distintos y no deben
 * mezclarse ni "unificarse" sin decisión legal previa.
 *
 * ⚠️ Revisión pendiente del equipo (no son decisiones técnicas):
 *  - El aviso de privacidad dirige los derechos ARCO a `hola@ilumetria.com`,
 *    pero todo el sitio usa `contacto@ilumetria.com`. Hay que confirmar cuál
 *    es el buzón vigente y unificar. **Conviene resolverlo antes de traducir
 *    de nuevo**, o se arregla dos veces.
 *  - Las fechas ("Febrero 2026") deben actualizarse al republicar si el texto
 *    cambia.
 */

export const legal = {
  /**
   * Leyenda de traducción de cortesía.
   *
   * Solo se muestra cuando el idioma activo no es el original (ver
   * `IDIOMA_ORIGINAL`), así que en español no aparece nunca. Vive igual aquí
   * porque es el texto fuente desde el que se traduce, y porque el tipo de los
   * diccionarios exige que todos tengan las mismas claves.
   */
  avisoTraduccion:
    'Esta es la versión original y vinculante de este documento. Las traducciones a otros idiomas son de cortesía.',
  verEnEspanol: 'Ver la versión en español',
  volverAlInicio: 'Volver al inicio',

  meta: {
    privacidad:
      'Aviso de Privacidad de ILUMETRIA conforme a la LFPDPPP: qué datos capturamos en eventos físicos y cómo ejercer tus derechos ARCO.',
    terminos:
      'Términos de Servicio de ILUMETRIA para el uso de su plataforma y hardware de inteligencia de datos en eventos.',
    cookies: 'Política de Cookies y seguimiento digital y pasivo de ILUMETRIA.',
    barbunnyPrivacidad:
      'Aviso de Privacidad de Bar Bunny, la app de fidelización operada por Ilumetria.',
    barbunnyTerminos:
      'Términos y Condiciones de la app de fidelización Bar Bunny, operada por Ilumetria.',
  },

  ilumetria: {
    privacidad: {
      title: 'Aviso de Privacidad',
      effectiveNote: 'Última actualización: 28 de agosto de 2026',
      intro: [
        'Conforme a la Ley Federal de Protección de Datos Personales en Posesión de los Particulares (LFPDPPP) de México.',
        'En ILUMETRIA, la privacidad de nuestros usuarios y asistentes a eventos es nuestra prioridad. Este aviso describe cómo capturamos y protegemos la información en entornos físicos mediante nuestra tecnología de inteligencia de datos, y cómo tratamos los datos de quienes participan en nuestros estudios de industria.',
      ],
      sections: [
        {
          title: '1. Identidad y Responsabilidad',
          paragraphs: [
            'ILUMETRIA es responsable del tratamiento de los datos recolectados, asegurando que el uso de tecnologías de rastreo pasivo se realice bajo los más altos estándares de seguridad y anonimización.',
          ],
        },
        {
          title: '2. Datos Recabados',
          paragraphs: [
            'Nuestra tecnología captura flujos de movimiento, tiempos de permanencia y perfiles profesionales generales. No recolectamos información personal sensible sin consentimiento explícito.',
          ],
        },
        {
          title: '3. Estudios de industria',
          paragraphs: [
            'Cuando usted participa en un estudio de industria de ILUMETRIA —como el estudio sobre cómo se miden hoy los eventos en México— recabamos su correo electrónico al inicio y, al final, su nombre y el de su organización, junto con las respuestas que decida darnos.',
            'Estos datos se tratan para tres finalidades primarias: conducir y analizar el estudio, guardar su avance para que pueda retomarlo si sale a medias, y enviarle el reporte de resultados cuando esté listo.',
            'Los resultados se publican de forma agregada. Ninguna respuesta se cita con su nombre ni con el de su organización sin su autorización expresa.',
          ],
        },
        {
          title: '4. Uso de sus datos de contacto para fines comerciales',
          paragraphs: [
            'Al cerrar el estudio le preguntamos, en una casilla independiente y desmarcada por omisión, si autoriza que le contactemos. Esa casilla es una finalidad adicional y no es necesaria para participar: si no la marca, el estudio se guarda igual y sus respuestas cuentan igual.',
            'Si la marca, su nombre, su correo, su organización y el estado que nos indicó se incorporan a nuestro directorio de contactos de la industria, que utilizamos para dar seguimiento comercial —entre otras cosas, para ofrecerle el piloto de medición sin costo si expresó interés en él—. Ese directorio es de uso interno de ILUMETRIA: no se vende, no se renta y no se transfiere a terceros.',
            'Puede retirar esta autorización en cualquier momento, sin que ello afecte la licitud del tratamiento previo, por el mismo medio previsto para los derechos ARCO. Al retirarla, sus datos de contacto se eliminan del directorio y sus respuestas permanecen en el estudio de forma disociada.',
          ],
        },
        {
          title: '5. Sus Derechos ARCO',
          paragraphs: [
            'Usted puede ejercer sus derechos de Acceso, Rectificación, Cancelación y Oposición mediante un correo a hola@ilumetria.com.',
          ],
        },
      ],
    } satisfies LegalDocument,

    terminos: {
      title: 'Términos de Servicio',
      effectiveNote: 'Vigentes para los Estados Unidos Mexicanos — Febrero 2026',
      intro: [],
      sections: [
        {
          title: '1. Aceptación de Términos',
          paragraphs: [
            'Al utilizar la plataforma o servicios de ILUMETRIA, los usuarios aceptan someterse a los presentes Términos y Condiciones, los cuales se rigen por las leyes civiles y mercantiles de México.',
          ],
        },
        {
          title: '2. Uso de la Tecnología',
          paragraphs: [
            'El cliente se compromete a utilizar el hardware y software de ILUMETRIA exclusivamente para los fines comerciales pactados, respetando la propiedad intelectual de la marca.',
          ],
        },
        {
          title: '3. Responsabilidad Limitada',
          paragraphs: [
            'ILUMETRIA provee inteligencia basada en datos estadísticos. No nos hacemos responsables por decisiones comerciales tomadas por el usuario, ni por eventos de fuerza mayor que afecten la recolección de datos en sitio.',
          ],
        },
        {
          title: '4. Jurisdicción',
          paragraphs: [
            'Para cualquier controversia, las partes se someten a la jurisdicción de los tribunales competentes en la Ciudad de México, renunciando a cualquier otro fuero.',
          ],
        },
      ],
    } satisfies LegalDocument,

    cookies: {
      title: 'Política de Cookies',
      effectiveNote: 'Política de Seguimiento Digital y Pasivo',
      intro: [],
      sections: [
        {
          title: '¿Qué son las cookies y tecnologías similares?',
          paragraphs: [
            'En nuestro portal web utilizamos cookies para mejorar su experiencia. En nuestros servicios físicos, utilizamos etiquetas electrónicas que funcionan de forma similar a una "cookie física" para entender el recorrido del visitante.',
          ],
        },
        {
          title: 'Tipos de Cookies que utilizamos',
          paragraphs: [
            'Esenciales: necesarias para el funcionamiento del dashboard.',
            'Analíticas: nos ayudan a entender el tráfico del sitio y del evento físico.',
            'De Personalización: permiten recordar sus preferencias de visualización de datos.',
          ],
        },
        {
          title: 'Control de su Privacidad',
          paragraphs: [
            'Usted puede configurar su navegador para rechazar nuestras cookies web, sin embargo, algunas funciones del sistema de inteligencia podrían verse limitadas.',
          ],
        },
      ],
    } satisfies LegalDocument,
  },

  barbunny: {
    privacidad: {
      title: 'Aviso de Privacidad — Bar Bunny',
      effectiveNote: 'Aplica a la app de fidelización Bar Bunny, operada por Ilumetria.',
      intro: [],
      sections: [
        {
          title: '1. Responsable del Tratamiento',
          paragraphs: [
            'Última actualización: agosto 2026. Bar Bunny S.A. de C.V. es la entidad responsable del tratamiento de los datos personales obtenidos a través de la aplicación de fidelización Cuponera Bar Bunny, disponible vía web, Google Play y App Store.',
          ],
        },
        {
          title: '2. Datos que Recabamos',
          paragraphs: [
            'Datos de contacto e identificación: nombre, teléfono, correo electrónico y fecha de nacimiento. Datos de perfil (opcionales): género, zona, ocupación, bebida/música/día favoritos y horario preferido. Datos de tu tarjeta de lealtad: ID de Tarjeta Bunny, tarjeta NFC vinculada. Actividad en el programa: historial de visitas, puntos, stamps y recompensas canjeadas. No solicitamos ni almacenamos identificaciones oficiales, datos de pago ni ubicación en tiempo real.',
          ],
        },
        {
          title: '3. Finalidad del Uso de Datos',
          paragraphs: [
            'Tus datos se emplean exclusivamente para crear y administrar tu cuenta, asignarte beneficios, gestionar tu tarjeta de stamps, prevenir duplicidad o fraude, verificar tu número celular y personalizar tu experiencia en cada visita a Bar Bunny.',
          ],
        },
        {
          title: '4. Con Quién Compartimos tus Datos',
          paragraphs: [
            'No vendemos ni compartimos tus datos personales con anunciantes ni con terceros para fines comerciales, y no realizamos rastreo publicitario entre apps o sitios web. Tus datos solo son procesados por nuestro propio equipo y por proveedores de infraestructura en la nube que operan el servicio (hosting y base de datos) bajo obligaciones de confidencialidad. Los enlaces a WhatsApp, Instagram y Facebook te dirigen a servicios de terceros regidos por sus propias políticas de privacidad.',
          ],
        },
        {
          title: '5. Dónde se Almacenan tus Datos',
          paragraphs: [
            'Tu información se guarda en servidores en la nube con cifrado en tránsito (HTTPS/TLS). Tu sesión y, en el Modo Demo, tu perfil de fidelidad, se guardan además localmente en el almacenamiento de tu navegador o dispositivo (localStorage) para mantenerte conectado entre visitas.',
          ],
        },
        {
          title: '6. Uso por Menores de Edad',
          paragraphs: [
            'El programa de fidelización de Bar Bunny está dirigido exclusivamente a personas mayores de 18 años. No recopilamos intencionalmente datos personales de menores de edad; si detectamos una cuenta de un menor, será eliminada.',
          ],
        },
        {
          title: '7. Plazo de Conservación',
          paragraphs: [
            'Conservamos tus datos mientras tu cuenta permanezca activa. Los puntos caducan tras 12 meses de inactividad contados desde tu última visita. Si eliminas tu cuenta, tus datos personales se borran de forma permanente de nuestros sistemas, salvo que la ley nos obligue a conservar cierta información por un plazo adicional.',
          ],
        },
        {
          title: '8. Tus Derechos y Eliminación de tu Cuenta',
          paragraphs: [
            'Puedes solicitar el acceso, rectificación, cancelación u oposición (derechos ARCO) del tratamiento de tus datos escribiendo a privacidad@barbunny.com o directamente en caja en Bar Bunny. También puedes eliminar tu cuenta y todos tus datos en cualquier momento, sin necesidad de contactarnos, desde Configuración → Eliminar mi Cuenta dentro de la app; esta acción es inmediata e irreversible.',
          ],
        },
        {
          title: '9. Seguridad de tu Información',
          paragraphs: [
            'Aplicamos medidas técnicas y organizativas razonables para proteger tus datos contra acceso no autorizado, pérdida o alteración, incluyendo comunicación cifrada (HTTPS) entre la app y nuestros servidores.',
          ],
        },
        {
          title: '10. Cambios a este Aviso',
          paragraphs: [
            'Podemos actualizar este Aviso de Privacidad periódicamente para reflejar cambios en el servicio o en la normativa aplicable. La fecha de "Última actualización" al inicio de este documento indica la versión vigente.',
          ],
        },
        {
          title: '11. Contacto',
          paragraphs: [
            'Para dudas sobre este Aviso de Privacidad o el tratamiento de tus datos, escríbenos a privacidad@barbunny.com.',
          ],
        },
      ],
    } satisfies LegalDocument,

    terminos: {
      title: 'Términos y Condiciones — Bar Bunny',
      effectiveNote: 'Aplica a la app de fidelización Bar Bunny, operada por Ilumetria.',
      intro: [],
      sections: [
        {
          title: '1. Aceptación de los Términos',
          paragraphs: [
            'Última actualización: julio 2026. Al registrarte, iniciar sesión o utilizar tu tarjeta NFC/digital en la plataforma Cuponera Bar Bunny, aceptas plenamente estos Términos y Condiciones de Uso.',
          ],
        },
        {
          title: '2. Requisito de Edad (18+)',
          paragraphs: [
            'El programa de fidelización y consumo en Bar Bunny requiere que el usuario cuente con al menos 18 años de edad y presente identificación oficial con fotografía (INE/Pasaporte) a solicitud del personal.',
          ],
        },
        {
          title: '3. Sistema de Puntos y Recompensas',
          paragraphs: [
            'Las compras y check-ins otorgan puntos y stamps según las reglas vigentes del programa, calculadas y validadas por el sistema de fidelización. Los puntos caducan tras 12 meses de inactividad contados desde tu última visita.',
          ],
        },
        {
          title: '4. Restricciones del Servicio',
          paragraphs: [
            'Los puntos y stamps acumulados no son canjeables por dinero en efectivo ni transferencia monetaria. Bar Bunny se reserva el derecho de anular puntos o cancelar membresías en caso de uso fraudulento.',
          ],
        },
      ],
    } satisfies LegalDocument,
  },

  /* ── Página de eliminación de cuenta (requisito de Google Play) ── */

  eliminarCuenta: {
    titulo: 'Eliminar tu cuenta de Bar Bunny',
    descripcion:
      'Cómo eliminar tu cuenta y tus datos personales de Bar Bunny, la app de fidelización operada por Ilumetria — desde la app o por correo.',
    intro: (entidad: string) =>
      `Esta página aplica a Bar Bunny, la app de fidelización operada por ${entidad}. Aquí explicamos cómo eliminar tu cuenta y qué pasa con tus datos al hacerlo.`,

    desdeLaApp: 'Desde la app',
    /**
     * Pasos dentro de la app móvil Bar Bunny (com.ilumetria.barbunny).
     * Verificados contra el código real de la app; edítalos si la navegación
     * cambia.
     */
    pasos: [
      'Abre la app Bar Bunny en tu teléfono e inicia sesión con tu cuenta.',
      'En la pantalla principal ("Mi Cuponera"), toca el ícono de engrane ⚙️ (Ajustes) en la esquina superior derecha.',
      'Dentro de Ajustes, desplázate hasta el final y toca "Eliminar Cuenta Definitivamente".',
      'Confirma la acción en el mensaje de advertencia. La eliminación se procesa de inmediato.',
    ],
    inmediata: 'Esta acción es inmediata e irreversible — no hace falta esperar a que alguien la procese.',

    sinApp: 'Si ya no tienes la app',
    sinAppAsunto: 'Eliminar mi cuenta de Bar Bunny',
    sinAppTexto: (correo: string) =>
      `Escríbenos a ${correo} solicitando la eliminación de tu cuenta. Incluye el número de teléfono con el que te registraste — es como identificamos tu cuenta.`,

    queSeElimina: 'Qué datos se eliminan',
    tabla: {
      seElimina: 'Se elimina',
      seConserva: 'Se conserva',
      porQue: 'Por qué',
    },

    plazo: 'Plazo',
    plazoTexto: 'Eliminación desde la app: inmediata e irreversible.',
    plazoCorreoSinSla:
      'Las solicitudes por correo se atienden en cuanto podemos confirmar que la cuenta es tuya.',

    /**
     * Plazo de respuesta a solicitudes por correo.
     *
     * `null` mientras el equipo no defina un SLA: la página omite la frase en
     * vez de publicar un plazo inventado (o, peor, un marcador de pendiente
     * visible para el usuario y para el revisor de Google Play).
     *
     * TODO (negocio): acordar el plazo con soporte y ponerlo aquí, p. ej.
     * 'Respondemos dentro de los 10 días hábiles siguientes.'
     */
    slaCorreo: null as string | null,

    /**
     * Nota sobre el plazo legal de conservación del historial anonimizado.
     *
     * TODO (legal): si existe un plazo obligatorio (fiscal, de protección de
     * datos) para el historial transaccional retenido, escribirlo aquí.
     */
    notaConservacionLegal: null as string | null,

    /**
     * Verificado contra el backend real: el mecanismo es anonimización, no
     * borrado físico de la fila del cliente (el ledger de puntos y el historial
     * de visitas referencian ese id y, por regla de negocio del propio backend,
     * "nunca se edita ni se borra").
     *
     * ⚠️ Refleja el backend ACTUAL. Si la migración a microservicios ya se
     * completó cuando leas esto, reverifica antes de publicar esta tabla.
     */
    filas: [
      {
        id: 'nombre',
        deleted: 'Nombre (se reemplaza por "Cliente eliminado")',
        retained: '—',
        reason: 'N/A',
      },
      {
        id: 'telefono',
        deleted: 'Teléfono (se reemplaza por un valor no reutilizable)',
        retained: '—',
        reason: 'N/A',
      },
      {
        id: 'identificacion',
        deleted:
          'Correo electrónico, fecha de nacimiento, género, zona, ocupación, usuario de Instagram',
        retained: '—',
        reason: 'N/A',
      },
      {
        id: 'verificacion',
        deleted: 'Estatus y foto/URL de verificación de identidad',
        retained: '—',
        reason: 'N/A',
      },
      {
        id: 'contrasena',
        deleted:
          'Contraseña (se sustituye por un valor aleatorio no utilizable — la cuenta no puede volver a iniciar sesión)',
        retained: '—',
        reason: 'N/A',
      },
      {
        id: 'preferencias',
        deleted:
          'Preferencias de perfil (bebida, música, día y horario favoritos, motivo de visita) — se eliminan por completo',
        retained: '—',
        reason: 'N/A',
      },
      {
        id: 'historial',
        deleted: '—',
        retained:
          'Historial de visitas, cupones y movimientos de puntos (ligados a un identificador ya anonimizado, no a tu nombre/teléfono real)',
        reason:
          'Integridad contable/operativa del programa de lealtad (reportes agregados de la sucursal); el propio sistema trata este historial como un registro que nunca se edita ni se borra, ligado a un id, no a tus datos personales ya eliminados',
      },
    ] satisfies DataRetentionRow[],
  },
};
